/**
 * 揭示面板（MISSING 补齐项）：配对环节 Reveal 之后，在作战台同时展示双方卡片摘要，
 * 便于辩手即时复盘「我方是否完成了卡片强制任务、对方流派是什么」。
 */
import type { RevealedCard, Side } from '@zongheng/core-schema';
import { sideLabel } from '@zongheng/core-schema';
import { Badge, cx, EmptyState } from '../ui/primitives.tsx';
import { cardSummary, cardTitle } from './OpponentPanel.tsx';

export function RevealPanel({
  reveals,
  pairId,
  stageIndex,
  selfSide,
}: {
  reveals: RevealedCard[];
  pairId: string | null;
  stageIndex: number;
  selfSide: Side;
}) {
  const pairReveals = pairId
    ? reveals.filter((reveal) => reveal.pairId === pairId)
    : reveals.filter((reveal) => reveal.stageIndex === stageIndex);

  return (
    <section
      data-testid="hud-reveal"
      aria-label="双盲揭示"
      className="rounded-xl border border-white/10 bg-bg-surface/80 p-3 backdrop-blur"
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-slate-100">双盲揭示 · 双方卡片摘要</h2>
        <Badge tone={pairReveals.length > 0 ? 'pass' : 'blind'}>
          {pairReveals.length > 0 ? '已揭示' : '待揭示'}
        </Badge>
      </div>
      {pairReveals.length === 0 ? (
        <EmptyState
          title="本配对尚未揭示"
          detail="发牌后仅本侧可见；本配对最后一个环节结束（或教师强制揭示）后，双方卡片摘要同时公开。"
        />
      ) : (
        <div className="grid gap-2 md:grid-cols-2">
          {(['AFF', 'NEG'] as const).map((side) => {
            const reveal = pairReveals.find((item) => item.side === side);
            return (
              <div
                key={side}
                data-testid={`hud-reveal-${side.toLowerCase()}`}
                className={cx(
                  'rounded-lg border px-3 py-2',
                  side === selfSide ? 'border-assert-pass/30 bg-assert-pass/5' : 'border-white/10 bg-black/20',
                  !reveal && 'opacity-60',
                )}
              >
                <div className="flex items-center gap-2">
                  <Badge tone={side === 'AFF' ? 'aff' : 'neg'}>{sideLabel(side)}</Badge>
                  {side === selfSide && <Badge tone="pass">我方</Badge>}
                  <span className="mono ml-auto text-[10px] text-slate-600">
                    环节 {reveal?.stageIndex ?? '—'}
                  </span>
                </div>
                {reveal ? (
                  <>
                    <p className="mt-1.5 text-sm font-medium text-slate-100">{cardTitle(reveal.card)}</p>
                    <p className="clamp-3 mt-1 text-xs leading-relaxed text-slate-400">
                      {cardSummary(reveal.card)}
                    </p>
                    <p className="mono mt-1 truncate text-[10px] text-slate-600" title={reveal.hash}>
                      commit {reveal.hash.slice(0, 18)}…
                    </p>
                  </>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">该侧本配对无卡片</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
